import ObjectHash from 'object-hash'

export const AccountModule = ({ name, retrieveUrl, removeUrl, createUrl, isBridge }) => {
	function buildCacheKey(companyId, params) {
		return `company:${companyId}:${name}Account:${ObjectHash(params)}`
	}

	function isInCache(state, companyId) {
		if (state.cacheKey !== buildCacheKey(companyId, {})) {
			return false
		}
		if (state.ttl === null || state.ttl < Date.now()) {
			return false
		}
		return true
	}

	const api = isBridge ? '$fetchCrmBridge' : '$api'

	return {
		state: () => {
			return {
				account: null,
				cacheDuration: 1800000, // 30 minutes
				cacheKey: null,
				ttl: null,
			}
		},
		getters: {
			isConnected(state) {
				return !!(state.account !== null && state.account.id)
			},

			account(state) {
				return state.account
			},
		},
		mutations: {
			assign(state, { data, companyId }) {
				state.account = data
				state.ttl = Date.now() + state.cacheDuration
				state.cacheKey = buildCacheKey(companyId, {})
			},

			clear(state) {
				state.account = null
				state.cacheKey = null
				state.ttl = null
			},

			invalidateCache(state) {
				state.ttl = null
			},
		},

		actions: {
			async retrieve({ state, commit }, { companyId }) {
				try {
					if (isInCache(state, companyId)) {
						return state.account
					}

					const res = await useNuxtApp()[api](retrieveUrl(companyId))

					let data = res

					if (isBridge) data = res.results?.length > 0 ? res.results[0] : {}

					if (data) commit('assign', { companyId, data })

					return res
				} catch (err) {
					// if not found is a 404, we don't want to throw an error, just clear the cache
					commit('clear')
					if (err.statusCode !== 404) throw err
				}
			},

			async remove({ commit, state }, { companyId }) {
				try {
					await useNuxtApp()[api](removeUrl(companyId, state.account.id), {
						method: 'DELETE',
					})
				} finally {
					commit('assign', {
						companyId,
						data: {
							id: null,
						},
					})
				}
			},

			async create({ commit }, { companyId, data, code }) {
				try {
					let body

					if (isBridge) {
						body = {
							extra_data: { code },
							company_id: companyId,
							provider: name,
						}
					} else {
						body = {
							...data,
							company: companyId,
						}
					}

					const res = await useNuxtApp()[api](createUrl(companyId), {
						method: 'POST',
						body,
					})

					if (res) {
						commit('assign', { companyId, data: res })
						return res
					}
				} catch (err) {
					commit('clear')
					throw err
				}
			},
		},
	}
}

export const ProviderModule = (providerName) => {
	return AccountModule({
		name: providerName,
		retrieveUrl: (companyId) =>
			`/integrations/?company_id=${companyId}&provider=${providerName}`,
		removeUrl: (_, accountId) => `/integrations/${accountId}/`,
		createUrl: () => `/integrations/`,
		isBridge: true,
	})
}
